/**
 * @description
 * Table-specific analytics insights component.
 * Shows relevant metrics and insights based on the selected table type.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { useMemo } from 'react';
import { 
  GitCommit, 
  MessageSquare, 
  Bot, 
  Hash, 
  TrendingUp,
  Users,
  Activity,
  Code,
  FileText,
  Zap
} from 'lucide-react';
import type { VSCodeLensTable, StreamRecord, UserAggregation, DomainAggregation, UserStatistics } from '../../../types/vscode-lens';

interface TableSpecificInsightsProps {
  table: VSCodeLensTable;
  data: StreamRecord[];
  userAggregation: UserAggregation | null;
  userStatistics: UserStatistics | null;
  domainAggregation: DomainAggregation | null;
}

/**
 * Table-specific insights component
 */
export function TableSpecificInsights({
  table,
  data,
  userAggregation,
  userStatistics,
  domainAggregation,
}: TableSpecificInsightsProps) {
  const insights = useMemo(() => {
    if (!data.length) return null;

    switch (table) {
      case 'commit_events':
        return getCommitInsights(data, userAggregation, userStatistics, domainAggregation);
      case 'conversation_messages':
        return getConversationInsights(data, userAggregation, userStatistics, domainAggregation);
      case 'llm_completions':
        return getLLMInsights(data, userAggregation, userStatistics, domainAggregation);
      default:
        return null;
    }
  }, [table, data, userAggregation, userStatistics, domainAggregation]);

  if (!insights) return null;

  return (
    <div className="rounded-lg border bg-card">
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          {insights.icon}
          <h3 className="text-lg font-semibold">{insights.title}</h3>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{insights.description}</p>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {insights.metrics.map((metric, index) => (
            <div key={index} className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">{metric.label}</p>
                <div className={`p-1.5 rounded-md ${metric.colorClass}`}>
                  <metric.icon className="h-3 w-3" />
                </div>
              </div>
              <p className="text-xl font-bold">{metric.value}</p>
              {metric.subtitle && (
                <p className="text-xs text-muted-foreground mt-1">{metric.subtitle}</p>
              )}
            </div>
          ))}
        </div>

        {/* Additional Insights */}
        {insights.additionalInsights && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Additional Insights</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {insights.additionalInsights.map((insight, index) => (
                <div key={index} className="rounded-md bg-muted/20 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <insight.icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{insight.title}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Extract repository name from repository path
 */
function extractRepoName(repositoryPath: unknown): string | null {
  if (typeof repositoryPath !== 'string') return null;
  
  // Handle different repository path formats
  // Examples: "user/repo", "org/repo", "https://github.com/user/repo", "/path/to/repo"
  const path = repositoryPath.trim();
  
  // Extract from GitHub URLs
  if (path.includes('github.com/')) {
    const match = path.match(/github\.com\/([^/]+\/[^/]+)/);
    if (match) return match[1];
  }
  
  // Extract from Git URLs
  if (path.includes('git@')) {
    const match = path.match(/git@[^:]+:([^/]+\/[^/]+)/);
    if (match) return match[1];
  }
  
  // Handle simple "user/repo" format
  if (path.includes('/') && !path.includes('://') && !path.startsWith('/')) {
    return path;
  }
  
  // Handle local paths - extract last directory name
  if (path.includes('/')) {
    const parts = path.split('/').filter(Boolean);
    return parts[parts.length - 1] || null;
  }
  
  return path || null;
}

/**
 * Extract branch name from various possible fields
 */
function extractBranchName(record: StreamRecord): string | null {
  // Try different possible branch field names
  const branchFields = [
    'branch_name',
    'branch', 
    'ref',
    'head_ref',
    'target_branch',
    'source_branch'
  ];
  
  for (const field of branchFields) {
    const value = record[field];
    if (typeof value === 'string' && value.trim()) {
      // Remove common prefixes like "refs/heads/"
      return value.replace(/^refs\/heads\//, '').trim();
    }
  }
  
  return null;
}

/**
 * Get insights for commit events table
 */
function getCommitInsights(
  data: StreamRecord[],
  userAggregation: UserAggregation | null,
  userStatistics: UserStatistics | null,
  domainAggregation: DomainAggregation | null
) {
  const totalCommits = data.length;
  
  // Extract repository names from repository_path
  const repoNames = data.map(record => {
    return extractRepoName(record.repository_path) || 
           record.repository_name || 
           record.repo_name || 
           null;
  }).filter(Boolean) as string[];
  
  const uniqueRepos = new Set(repoNames).size;
  
  // Extract branch names
  const branchNames = data.map(record => extractBranchName(record)).filter(Boolean) as string[];
  const uniqueBranches = new Set(branchNames).size;
  
  // Calculate average files changed per commit
  const filesChangedData = data.map(record => {
    // Try multiple possible field names for files changed
    const filesChanged = record.files_changed || record.files_modified || record.files_count || record.changed_files || 0;
    return typeof filesChanged === 'number' ? filesChanged : 0;
  }).filter(val => val > 0);
  
  const avgFilesChanged = filesChangedData.length > 0 
    ? Math.round(filesChangedData.reduce((sum, val) => sum + val, 0) / filesChangedData.length)
    : 0;

  // Calculate average lines changed
  const linesAdded = data.map(record => {
    const added = record.lines_added || record.additions || record.insertions || 0;
    return typeof added === 'number' ? added : 0;
  }).filter(val => val > 0);
  
  const linesDeleted = data.map(record => {
    const deleted = record.lines_deleted || record.deletions || record.removals || 0;
    return typeof deleted === 'number' ? deleted : 0;
  }).filter(val => val > 0);
  
  const avgLinesAdded = linesAdded.length > 0 ? Math.round(linesAdded.reduce((sum, val) => sum + val, 0) / linesAdded.length) : 0;
  const avgLinesDeleted = linesDeleted.length > 0 ? Math.round(linesDeleted.reduce((sum, val) => sum + val, 0) / linesDeleted.length) : 0;

  // Most active user from statistics
  const mostActiveUser = userStatistics?.mostActiveUser;
  
  // Calculate most active user by commits (alternative to domain)
  const userCommitCounts = data.reduce((acc, record) => {
    const email = record.user_email || record.author_email || record.committer_email;
    if (email && typeof email === 'string') {
      acc[email] = (acc[email] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
  
  const mostActiveUserByCommits = Object.entries(userCommitCounts)
    .sort(([,a], [,b]) => b - a)[0];

  // Get top repositories and branches
  const repoCounts = repoNames.reduce((acc, repo) => {
    acc[repo] = (acc[repo] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const branchCounts = branchNames.reduce((acc, branch) => {
    acc[branch] = (acc[branch] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const topRepos = Object.entries(repoCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3)
    .map(([repo, count]) => `${repo} (${count})`)
    .join(', ');

  const topBranches = Object.entries(branchCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 3)
    .map(([branch, count]) => `${branch} (${count})`)
    .join(', ');

  return {
    icon: <GitCommit className="h-5 w-5 text-primary" />,
    title: 'Commit Activity Insights',
    description: 'Analysis of code commit patterns and developer activity',
    metrics: [
      {
        icon: Hash,
        label: 'Total Commits',
        value: totalCommits.toLocaleString(),
        colorClass: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
      },
      {
        icon: Code,
        label: 'Unique Repositories',
        value: uniqueRepos.toString(),
        colorClass: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
      },
      {
        icon: GitCommit,
        label: 'Unique Branches',
        value: uniqueBranches.toString(),
        colorClass: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
      },
      {
        icon: FileText,
        label: 'Avg Files/Commit',
        value: avgFilesChanged.toString(),
        colorClass: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300',
      },
      {
        icon: TrendingUp,
        label: 'Avg Lines Added',
        value: avgLinesAdded.toString(),
        colorClass: 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300',
      },
      {
        icon: TrendingUp,
        label: 'Avg Lines Deleted',
        value: avgLinesDeleted.toString(),
        colorClass: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      },
    ],
    additionalInsights: [
      {
        icon: Users,
        title: 'Most Active Developer',
        description: mostActiveUser 
          ? `${mostActiveUser.user} (${mostActiveUser.count} commits)`
          : mostActiveUserByCommits
          ? `${mostActiveUserByCommits[0]} (${mostActiveUserByCommits[1]} commits)`
          : 'No data available',
      },
      {
        icon: Users,
        title: 'Most Active User by Commits',
        description: mostActiveUserByCommits
          ? `${mostActiveUserByCommits[0]} (${mostActiveUserByCommits[1]} commits)`
          : 'No data available',
      },
      {
        icon: Code,
        title: 'Top Repositories',
        description: topRepos || 'No repository data available',
      },
      {
        icon: GitCommit,
        title: 'Top Branches',
        description: topBranches || 'No branch data available',
      },
    ],
  };
}

/**
 * Get insights for conversation messages table
 */
function getConversationInsights(
  data: StreamRecord[],
  userAggregation: UserAggregation | null,
  userStatistics: UserStatistics | null,
  domainAggregation: DomainAggregation | null
) {
  const totalMessages = data.length;
  const uniqueConversations = new Set(data.map(record => record.conversation_id || record.session_id).filter(Boolean)).size;
  
  // Calculate average message length
  const messageLengths = data.map(record => {
    const content = record.content || record.message || record.text || '';
    return typeof content === 'string' ? content.length : 0;
  }).filter(length => length > 0);
  const avgMessageLength = messageLengths.length > 0 
    ? Math.round(messageLengths.reduce((sum, length) => sum + length, 0) / messageLengths.length)
    : 0;

  // Count message types
  const messageTypes = data.map(record => record.message_type || record.type || 'unknown');
  const typeCounts = messageTypes.reduce((acc, type) => {
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const mostCommonType = Object.entries(typeCounts).reduce((a, b) => a[1] > b[1] ? a : b, ['unknown', 0]);

  // Most active user from statistics
  const mostActiveUser = userStatistics?.mostActiveUser;

  return {
    icon: <MessageSquare className="h-5 w-5 text-primary" />,
    title: 'Conversation Activity Insights',
    description: 'Analysis of chat messages and communication patterns',
    metrics: [
      {
        icon: MessageSquare,
        label: 'Total Messages',
        value: totalMessages.toLocaleString(),
        colorClass: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
      },
      {
        icon: Hash,
        label: 'Unique Conversations',
        value: uniqueConversations.toString(),
        colorClass: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
      },
      {
        icon: FileText,
        label: 'Avg Message Length',
        value: `${avgMessageLength} chars`,
        colorClass: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
      },
      {
        icon: Activity,
        label: 'Most Common Type',
        value: mostCommonType[0],
        subtitle: `${mostCommonType[1]} messages`,
        colorClass: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300',
      },
    ],
    additionalInsights: [
      {
        icon: Users,
        title: 'Most Active Participant',
        description: mostActiveUser 
          ? `${mostActiveUser.user} (${mostActiveUser.count} messages)`
          : 'No data available',
      },
      {
        icon: Users,
        title: 'Most Active User by Messages',
        description: mostActiveUser 
          ? `${mostActiveUser.user} (${mostActiveUser.count} messages)`
          : 'No data available',
      },
    ],
  };
}

/**
 * Get insights for LLM completions table
 */
function getLLMInsights(
  data: StreamRecord[],
  userAggregation: UserAggregation | null,
  userStatistics: UserStatistics | null,
  domainAggregation: DomainAggregation | null
) {
  const totalCompletions = data.length;
  const uniqueModels = new Set(data.map(record => record.model || record.llm_model).filter(Boolean)).size;
  
  // Calculate average prompt and completion lengths
  const promptLengths = data.map(record => {
    const prompt = record.prompt || record.input || record.query || '';
    return typeof prompt === 'string' ? prompt.length : 0;
  }).filter(length => length > 0);
  const avgPromptLength = promptLengths.length > 0 
    ? Math.round(promptLengths.reduce((sum, length) => sum + length, 0) / promptLengths.length)
    : 0;

  const completionLengths = data.map(record => {
    const completion = record.completion || record.output || record.response || '';
    return typeof completion === 'string' ? completion.length : 0;
  }).filter(length => length > 0);
  const avgCompletionLength = completionLengths.length > 0 
    ? Math.round(completionLengths.reduce((sum, length) => sum + length, 0) / completionLengths.length)
    : 0;

  // Calculate average tokens (if available)
  const tokenCounts = data.map(record => record.tokens || record.total_tokens || 0).filter(val => typeof val === 'number');
  const avgTokens = tokenCounts.length > 0 
    ? Math.round(tokenCounts.reduce((sum, val) => sum + val, 0) / tokenCounts.length)
    : 0;

  // Most common model
  const models = data.map(record => record.model || record.llm_model || 'unknown');
  const modelCounts = models.reduce((acc, model) => {
    acc[model] = (acc[model] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const mostCommonModel = Object.entries(modelCounts).reduce((a, b) => a[1] > b[1] ? a : b, ['unknown', 0]);

  // Most active user from statistics
  const mostActiveUser = userStatistics?.mostActiveUser;

  return {
    icon: <Bot className="h-5 w-5 text-primary" />,
    title: 'LLM Usage Insights',
    description: 'Analysis of AI model usage and completion patterns',
    metrics: [
      {
        icon: Bot,
        label: 'Total Completions',
        value: totalCompletions.toLocaleString(),
        colorClass: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
      },
      {
        icon: Hash,
        label: 'Unique Models',
        value: uniqueModels.toString(),
        colorClass: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
      },
      {
        icon: FileText,
        label: 'Avg Prompt Length',
        value: `${avgPromptLength} chars`,
        colorClass: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
      },
      {
        icon: FileText,
        label: 'Avg Completion Length',
        value: `${avgCompletionLength} chars`,
        colorClass: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300',
      },
      {
        icon: Zap,
        label: 'Avg Tokens',
        value: avgTokens > 0 ? avgTokens.toString() : 'N/A',
        colorClass: 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300',
      },
      {
        icon: Activity,
        label: 'Most Used Model',
        value: mostCommonModel[0],
        subtitle: `${mostCommonModel[1]} completions`,
        colorClass: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      },
    ],
    additionalInsights: [
      {
        icon: Users,
        title: 'Most Active User',
        description: mostActiveUser 
          ? `${mostActiveUser.user} (${mostActiveUser.count} completions)`
          : 'No data available',
      },
      {
        icon: Users,
        title: 'Most Active User by Completions',
        description: mostActiveUser 
          ? `${mostActiveUser.user} (${mostActiveUser.count} completions)`
          : 'No data available',
      },
    ],
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
